import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/Button';
import { PaymentRecord } from '@/types/domain/payment-management';
import { formatCurrency } from '@/lib/utils';
import {
  Database,
  X,
  RefreshCw,
  CheckCircle2,
  AlertTriangle,
  Code2,
} from 'lucide-react';

interface PaymentSapSyncModalProps {
  isOpen: boolean;
  payment: PaymentRecord;
  onClose: () => void;
  onSync: () => Promise<{ success: boolean; sapDocumentNumber: string }>;
}

export const PaymentSapSyncModal: React.FC<PaymentSapSyncModalProps> = ({
  isOpen,
  payment,
  onClose,
  onSync,
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [syncedDoc, setSyncedDoc] = useState<string | null>(payment.sapDocumentNumber || null);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      setErrorMessage(null);
      setSyncedDoc(payment.sapDocumentNumber || null);
    } else {
      document.body.style.overflow = '';
    }
  }, [isOpen, payment]);

  if (!isOpen) return null;

  const handleTriggerSync = async () => {
    try {
      setIsSubmitting(true);
      setErrorMessage(null);
      const res = await onSync();
      setSyncedDoc(res.sapDocumentNumber);
    } catch (err: any) {
      setErrorMessage(err?.message || 'SAP F110 clearing posting simulation failed.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const simulatedBapiPayload = {
    FUNCTION: 'BAPI_ACC_DOCUMENT_POST',
    HEADER: {
      BUS_ACT: 'RFBU',
      USERNAME: 'PROCUREAI_TREASURY_RFC',
      HEADER_TXT: `SAP F110 Clearing ${payment.paymentReference}`,
      COMP_CODE: payment.companyCode || '1000',
      DOC_DATE: payment.paymentDate,
      PSTNG_DATE: new Date().toISOString().slice(0, 10),
      DOC_TYPE: 'KZ', // Vendor Payment Document
    },
    ACCOUNT_PAYABLE: [
      {
        ITEMNO_ACC: 1,
        VENDOR_NO: payment.vendorCode,
        GL_ACCOUNT: '0000160000', // Accounts Payable Domestic
        DIFF_INV: payment.invoiceNumber,
        AMOUNT: -payment.amount,
        CURRENCY: payment.currency,
      },
    ],
    ACCOUNT_BANK: [
      {
        ITEMNO_ACC: 2,
        HOUSE_BANK: 'HDFC1',
        ACCOUNT_ID: 'INR01',
        GL_ACCOUNT: '0000113100', // Bank Outgoing Clearing
        AMOUNT: payment.amount,
        CURRENCY: payment.currency,
      },
    ],
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="payment-sap-sync-modal-title"
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-background/80 backdrop-blur-xs animate-in fade-in duration-200"
    >
      <div className="relative w-full max-w-xl bg-card border border-border rounded-2xl shadow-xl overflow-hidden font-heading flex flex-col max-h-[90vh]">
        {/* Header */}
        <div className="p-5 border-b border-border flex items-center justify-between bg-muted/20">
          <div className="flex items-center gap-3">
            <div className="p-2.5 rounded-xl bg-primary/10 text-primary">
              <Database className="h-5 w-5" />
            </div>
            <div>
              <h2 id="payment-sap-sync-modal-title" className="text-base font-bold text-foreground">
                SAP S/4HANA F110 Payment Run Clearing
              </h2>
              <p className="text-xs text-foreground-muted">
                Simulated BAPI RFC Clearing Integration — Doc Type KZ (Vendor Payment)
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-foreground-muted hover:text-foreground rounded-lg transition-colors cursor-pointer"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-4 overflow-y-auto flex-1 text-xs">
          {errorMessage && (
            <div className="p-3 bg-rose-500/10 border border-rose-500/30 rounded-xl flex items-start gap-2.5 text-rose-700 dark:text-rose-400">
              <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
              <span>{errorMessage}</span>
            </div>
          )}

          {syncedDoc && (
            <div className="p-3.5 bg-emerald-500/10 border border-emerald-500/30 rounded-xl flex items-center gap-3 text-emerald-700 dark:text-emerald-400">
              <CheckCircle2 className="h-5 w-5 shrink-0" />
              <div>
                <span className="font-bold block">Disbursement Cleared in SAP S/4HANA</span>
                <span className="font-mono text-xs">
                  Financial Document No: <strong>{syncedDoc}</strong> (Fiscal Year {new Date().getFullYear()})
                </span>
              </div>
            </div>
          )}

          {/* Details Table */}
          <div className="p-3.5 bg-muted/40 rounded-xl border border-border space-y-2">
            <div className="flex justify-between">
              <span className="text-foreground-muted">ProcureAI Payment Ref:</span>
              <span className="font-bold text-foreground">{payment.paymentReference}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-foreground-muted">Vendor Account:</span>
              <span className="font-bold text-foreground">{payment.vendorName} ({payment.vendorCode})</span>
            </div>
            <div className="flex justify-between">
              <span className="text-foreground-muted">Source Invoice Ref:</span>
              <span className="font-bold text-purple-600">{payment.invoiceNumber}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-foreground-muted">Disbursement Amount:</span>
              <span className="font-extrabold text-emerald-600">
                {formatCurrency(payment.amount, payment.currency)}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-foreground-muted">Clearing Method:</span>
              <span className="font-bold text-foreground">{payment.paymentMethod}</span>
            </div>
          </div>

          {/* BAPI Payload Preview */}
          <div className="space-y-1.5">
            <div className="flex items-center justify-between text-foreground-muted text-[11px] font-bold">
              <span className="flex items-center gap-1.5">
                <Code2 className="h-3.5 w-3.5 text-primary" />
                Simulated BAPI_ACC_DOCUMENT_POST Payload
              </span>
              <span className="text-[10px] text-foreground-muted">SAP S/4HANA 2023 FI-AP</span>
            </div>
            <pre className="p-3 bg-slate-950 text-emerald-400 rounded-xl font-mono text-[10px] overflow-x-auto border border-slate-800 leading-relaxed">
              {JSON.stringify(simulatedBapiPayload, null, 2)}
            </pre>
          </div>
        </div>

        {/* Footer */}
        <div className="p-5 border-t border-border flex items-center justify-between bg-muted/10">
          <span className="text-[11px] text-foreground-muted italic">
            Simulated BAPI execution: updates central mock ledger.
          </span>
          <div className="flex gap-2.5">
            <Button variant="outline" size="sm" onClick={onClose} disabled={isSubmitting}>
              Close
            </Button>
            <Button size="sm" onClick={handleTriggerSync} disabled={isSubmitting || Boolean(syncedDoc)}>
              {isSubmitting ? (
                <>
                  <RefreshCw className="h-4 w-4 animate-spin mr-1.5" /> Posting to SAP...
                </>
              ) : syncedDoc ? (
                'Already Cleared'
              ) : (
                'Execute SAP F110 Post'
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
